// 税筹派发与利润回填相关 API

import request, { get, post, put, del } from '@/api/request'

/**
 * 银行卡相关 API
 */

/**
 * 查询已绑定银行卡的卡主列表（创建任务时选择卡主用）
 * @returns {Promise<Array<{ownerType: number, ownerId: number, ownerName: string}>>}
 */
export function getBankCardOwners() {
  return get('/tax-planning/bank-cards/owners')
}

/**
 * 分页查询银行卡
 * @param {Object} data - 查询参数
 * @param {number} data.page - 当前页码
 * @param {number} data.size - 每页大小
 * @param {number} [data.ownerType] - 卡主类型：1-系统用户，2-外部人员
 * @param {number} [data.ownerId] - 卡主ID
 * @param {number} [data.status] - 状态：1-启用，0-停用
 * @param {number} [data.accountType] - 账户类型：1-出账账户，2-入账账户
 * @param {string} [data.keyword] - 持卡人/卡号关键字
 * @returns {Promise<{records: Array, total: number}>}
 */
export function getBankCardPage(data) {
  return post('/tax-planning/bank-cards/page', data)
}

/**
 * 新增银行卡
 * @param {Object} data - { ownerType, ownerId, accountType, holderName, bankName, branchName, cardNo, dailyOutLimit, dailyInLimit, remark }
 * @param {number} data.accountType - 账户类型：1-出账账户，2-入账账户（必填）
 */
export function createBankCard(data) {
  return post('/tax-planning/bank-cards', data)
}

/**
 * 编辑银行卡
 * @param {number} id - 银行卡ID
 * @param {Object} data - 同新增字段（含必填的 accountType：1-出账账户，2-入账账户）
 */
export function updateBankCard(id, data) {
  return put(`/tax-planning/bank-cards/${id}`, data)
}

/**
 * 启用/停用银行卡
 * @param {number} id - 银行卡ID
 * @param {number} status - 状态：1-启用，0-停用
 */
export function updateBankCardStatus(id, status) {
  return put(`/tax-planning/bank-cards/${id}/status?status=${status}`)
}

/**
 * 删除银行卡
 * @param {number} id - 银行卡ID
 */
export function deleteBankCard(id) {
  return del(`/tax-planning/bank-cards/${id}`)
}

/**
 * 查询可用银行卡（按金额与方向筛选当日剩余额度充足的卡）
 * @param {Object} params - 查询参数
 * @param {number} params.amount - 本笔金额
 * @param {string} params.direction - 方向：OUT-出账，IN-入账
 * @returns {Promise<Array>} 可用卡列表，含 id/holderName/bankName/cardNo/dailyLimit/todayUsed/remainingQuota/ownerType/ownerId/ownerName
 */
export function getAvailableBankCards(params) {
  return get('/tax-planning/bank-cards/available', params)
}

/**
 * 资金任务相关 API
 */

/**
 * 分页查询资金任务
 * @param {Object} data - 查询参数
 * @param {number} data.page - 当前页码
 * @param {number} data.size - 每页大小
 * @param {number} [data.taskType] - 任务类型：1-税筹派发，2-利润回填
 * @param {number} [data.status] - 状态：0-待处理，1-处理中，2-已完成，3-已取消
 * @param {number} [data.applicantUserId] - 申请人用户ID
 * @param {string} [data.startDate] - 开始日期
 * @param {string} [data.endDate] - 结束日期
 * @returns {Promise<{records: Array, total: number}>}
 */
export function getFundTaskPage(data) {
  return post('/tax-planning/fund-tasks/page', data)
}

/**
 * 创建资金任务
 * @param {Object} data - { taskType, cardId, totalAmount, remark }（经办人由后端取当前登录人）
 */
export function createFundTask(data) {
  return post('/tax-planning/fund-tasks', data)
}

/**
 * 取消资金任务
 * @param {number} id - 任务ID
 */
export function cancelFundTask(id) {
  return put(`/tax-planning/fund-tasks/${id}/cancel`)
}

/**
 * 获取资金任务详情
 * @param {number} id - 任务ID
 * @returns {Promise<{task: Object, records: Array}>}
 */
export function getFundTaskDetail(id) {
  return get(`/tax-planning/fund-tasks/${id}`)
}

/**
 * 生成操作记录
 * @param {number} id - 任务ID
 * @param {Object} data - { cardId, amount, remark }
 */
export function createFundTaskRecord(id, data) {
  return post(`/tax-planning/fund-tasks/${id}/records`, data)
}

/**
 * 取消操作记录
 * @param {number} recordId - 记录ID
 */
export function cancelFundTaskRecord(recordId) {
  return put(`/tax-planning/fund-tasks/records/${recordId}/cancel`)
}

/**
 * 打款确认
 * @param {number} recordId - 记录ID
 * @param {Object} data - { amount, remark }
 */
export function confirmFundTaskRecordPay(recordId, data) {
  return put(`/tax-planning/fund-tasks/records/${recordId}/confirm-pay`, data)
}

/**
 * 收款确认
 * @param {number} recordId - 记录ID
 * @param {Object} data - { amount, remark }
 */
export function confirmFundTaskRecordReceive(recordId, data) {
  return put(`/tax-planning/fund-tasks/records/${recordId}/confirm-receive`, data)
}

/**
 * 修改操作记录金额（修正误操作）
 * @param {number} recordId - 记录ID
 * @param {Object} data - { amount, remark }
 */
export function updateFundTaskRecordAmount(recordId, data) {
  return put(`/tax-planning/fund-tasks/records/${recordId}/update-amount`, data)
}

/**
 * 导出资金任务列表
 * @param {Object} params - 查询参数（同分页查询的筛选条件）
 * @returns {Promise<Blob>}
 */
export function exportFundTaskList(params) {
  return request.get('/tax-planning/fund-tasks/export', { params, responseType: 'blob' })
}

/**
 * 导出资金任务操作明细
 * @param {number} id - 任务ID
 * @returns {Promise<Blob>}
 */
export function exportFundTaskRecords(id) {
  return request.get(`/tax-planning/fund-tasks/${id}/records/export`, { responseType: 'blob' })
}

/**
 * 外部人员相关 API
 */

/**
 * 分页查询外部人员
 * @param {Object} data - 查询参数
 * @param {number} data.page - 当前页码
 * @param {number} data.size - 每页大小
 * @param {string} [data.keyword] - 姓名/手机号/证件号关键字
 * @param {number} [data.status] - 状态：1-启用，0-停用
 * @returns {Promise<{records: Array, total: number}>}
 */
export function getExternalPersonPage(data) {
  return post('/tax-planning/external-persons/page', data)
}

/**
 * 新增外部人员
 * @param {Object} data - { name, phone, idCardNo, remark }
 */
export function createExternalPerson(data) {
  return post('/tax-planning/external-persons', data)
}

/**
 * 编辑外部人员
 * @param {number} id - 外部人员ID
 * @param {Object} data - 同新增字段
 */
export function updateExternalPerson(id, data) {
  return put(`/tax-planning/external-persons/${id}`, data)
}

/**
 * 启用/停用外部人员
 * @param {number} id - 外部人员ID
 * @param {number} status - 状态：1-启用，0-停用
 */
export function updateExternalPersonStatus(id, status) {
  return put(`/tax-planning/external-persons/${id}/status?status=${status}`)
}

/**
 * 查询启用状态外部人员下拉列表（选择器数据源）
 * @returns {Promise<Array<{id: number, name: string, phone: string}>>}
 */
export function getExternalPersonOptions() {
  return get('/tax-planning/external-persons/options')
}
